/**
 * PiniaColadaIndexedDB — persists successful query data to IndexedDB and prepopulates on ensure.
 *
 * Behavior
 * - Saves data on successful fetch/refresh and when setQueryData() is called
 * - Prepopulates data when a key is ensured and has no in-memory data
 * - Optionally background-refetches if the persisted record is older than the effective TTL
 *
 * Per-query options (module-augmented):
 *   - persist?: boolean (default: false, or plugin's defaultPersist value)
 *   - persistTtl?: number | 'staleTime' (default: 'staleTime')
 */

import type { PiniaColadaPlugin, UseQueryEntry } from '@pinia/colada'
import { createStore, del, get, set } from 'idb-keyval'
import { err, ok, type Result, ResultAsync } from 'neverthrow'
import { hash } from 'ohash'
import type { MaybeRefOrGetter } from 'vue'
import { toValue } from 'vue'

/**
 * Stored record shape for IndexedDB
 */
interface PersistRecord {
  data: unknown
  savedAt: number
}

export interface PiniaColadaIndexedDBOptions {
  /** IndexedDB database name */
  dbName?: string
  /** IndexedDB object store name */
  storeName?: string
  /** Prefix used before hashed query keys. Keep it short. */
  keyPrefix?: string
  /** Custom persistence filter; default: only when status === 'success' */
  shouldPersist?: (
    entry: UseQueryEntry,
    opts: PiniaColadaIndexedDBOptions,
  ) => boolean
  /** Default persistence behavior for queries that don't specify persist option. Default: false */
  defaultPersist?: boolean
  /** If true, prepopulate from IndexedDB during ensure when memory has no data. Default: true */
  prepopulateOnEnsure?: boolean
  /**
   * Background refetch policy after prepopulate.
   * - boolean: always/never refetch after prepopulate
   * - function: decide per entry
   * - undefined: refetch only when persisted record is older than ttl
   */
  backgroundRefetchOnPrepopulate?: boolean | ((entry: UseQueryEntry) => boolean)
  /** Whether to delete persisted entries on invalidate/remove. Default: false */
  removeOnInvalidate?: boolean
  /**
   * TTL used for background-refetch staleness check. Default: 'staleTime' (per-query).
   * If number, interpreted as milliseconds.
   */
  ttlMs?: number | 'staleTime'
  /** Enable console debug logging */
  debug?: boolean
}

// Per-query ergonomics via module augmentation
declare module '@pinia/colada' {
  interface UseQueryOptions {
    /** Enable/disable persistence for this query (default: false, or plugin's defaultPersist value) */
    persist?: MaybeRefOrGetter<boolean>
    /** TTL used for background refetch decision (default: 'staleTime') */
    persistTtl?: MaybeRefOrGetter<number | 'staleTime'>
  }
}

export const DEBUG_LABEL = '[PiniaColadaIndexedDBPlugin]'

export function PiniaColadaIndexedDB(
  opts: PiniaColadaIndexedDBOptions = {},
): PiniaColadaPlugin {
  const {
    dbName = 'pinia-colada',
    storeName = 'default',
    keyPrefix = '',
    shouldPersist,
    defaultPersist = false,
    prepopulateOnEnsure = true,
    backgroundRefetchOnPrepopulate,
    removeOnInvalidate = false,
    ttlMs = 'staleTime',
    debug = false,
  } = opts

  const canUseIDB = typeof indexedDB !== 'undefined'
  const store = canUseIDB ? createStore(dbName, storeName) : undefined

  const log = (...a: unknown[]) => {
    if (debug) console.debug(DEBUG_LABEL, ...a)
  }

  const serialize = (keyArr: readonly unknown[]) =>
    keyPrefix ? `${keyPrefix}:${hash(keyArr)}` : hash(keyArr)

  const resolvePersistEnabled = (entry: UseQueryEntry): boolean => {
    const persistValue = entry?.options?.persist
    return persistValue !== undefined ? toValue(persistValue) : defaultPersist
  }

  const getEffectiveTtlMs = (entry: UseQueryEntry): number => {
    const perQuery = entry?.options?.persistTtl
    const resolvedPerQuery = perQuery !== undefined ? toValue(perQuery) : ttlMs

    if (resolvedPerQuery === 'staleTime') {
      return entry?.options?.staleTime ?? 0
    }
    return typeof resolvedPerQuery === 'number' ? resolvedPerQuery : 0
  }

  const shouldBackgroundRefetch = (
    entry: UseQueryEntry,
    savedAt: number,
  ): boolean => {
    if (typeof backgroundRefetchOnPrepopulate === 'function') {
      return backgroundRefetchOnPrepopulate(entry)
    }
    if (typeof backgroundRefetchOnPrepopulate === 'boolean') {
      return backgroundRefetchOnPrepopulate
    }

    const ttl = getEffectiveTtlMs(entry)
    return ttl > 0 && Date.now() - savedAt > ttl
  }

  const persist = async (
    keyArr: readonly unknown[],
    data: unknown,
  ): Promise<Result<void, Error>> => {
    if (!canUseIDB || !store) {
      const error = new Error(
        'IndexedDB not available or store not initialized',
      )
      log('persist error', error.message)
      return err(error)
    }

    const idbKey = serialize(keyArr)
    const record: PersistRecord = { data, savedAt: Date.now() }

    return ResultAsync.fromPromise(
      set(idbKey, record, store),
      (error) => new Error(`Failed to persist ${idbKey}: ${error}`),
    )
      .map(() => {
        log('persisted', idbKey)
        return undefined
      })
      .mapErr((error) => {
        log('persist error', idbKey, error.message)
        return error
      })
  }

  const remove = async (
    keyArr: readonly unknown[],
  ): Promise<Result<void, Error>> => {
    if (!canUseIDB || !store) {
      const error = new Error(
        'IndexedDB not available or store not initialized',
      )
      log('remove error', error.message)
      return err(error)
    }

    const idbKey = serialize(keyArr)
    return ResultAsync.fromPromise(
      del(idbKey, store),
      (error) => new Error(`Failed to delete ${idbKey}: ${error}`),
    )
      .map(() => {
        log('deleted', idbKey)
        return undefined
      })
      .mapErr((error) => {
        log('remove error', idbKey, error.message)
        return error
      })
  }

  const prepopulate = async (
    queryCache: any,
    entry: UseQueryEntry,
  ): Promise<Result<void, Error>> => {
    if (!canUseIDB || !store || !resolvePersistEnabled(entry)) {
      return ok(undefined)
    }

    // Skip if entry already has data
    if (entry.state.value.data !== undefined) {
      return ok(undefined)
    }

    const idbKey = serialize(entry.key)

    return ResultAsync.fromPromise(
      get(idbKey, store) as Promise<PersistRecord | undefined>,
      (error) => new Error(`Failed to get ${idbKey} from IndexedDB: ${error}`),
    )
      .andThen((record) => {
        if (!record) {
          return ok(undefined)
        }

        return ResultAsync.fromPromise(
          Promise.resolve(queryCache.setQueryData(entry.key, record.data)),
          (error) =>
            new Error(`Failed to set query data for ${idbKey}: ${error}`),
        ).map(() => {
          log('prepopulated', idbKey)

          // Background refetch if needed
          if (shouldBackgroundRefetch(entry, record.savedAt)) {
            queryCache.fetch(entry).catch(() => {}) // fire-and-forget
          }

          return undefined
        })
      })
      .mapErr((error) => {
        log('prepopulate error', idbKey, error.message)
        return error
      })
  }

  const persistIfAllowed = async (
    entry: UseQueryEntry,
  ): Promise<Result<void, Error>> => {
    if (!resolvePersistEnabled(entry)) {
      return ok(undefined)
    }

    const status = entry.state.value.status
    const shouldPersistEntry =
      shouldPersist?.(entry, opts) ?? status === 'success'

    if (shouldPersistEntry) {
      return persist(entry.key, entry.state.value.data)
    }

    return ok(undefined)
  }

  return ({ queryCache }) => {
    if (!canUseIDB) {
      log('indexedDB not available; plugin inert')
      return
    }

    queryCache.$onAction(({ name, args, after }) => {
      switch (name) {
        case 'ensure': {
          after((entry: UseQueryEntry) => {
            if (!prepopulateOnEnsure) return
            void prepopulate(queryCache, entry)
          })
          break
        }
        case 'fetch':
        case 'refresh': {
          const entry = args[0]
          after(() => {
            void persistIfAllowed(entry)
          })
          break
        }
        case 'setQueryData': {
          const keyArr = args[0]
          const data = args[1]
          after(() => {
            // Since we only have the keyArr from setQueryData, we can't call ensure
            // which requires full UseQueryOptions. We'll persist directly since
            // setQueryData is an explicit user action indicating they want this data cached.
            void persist(keyArr, data)
          })
          break
        }
        case 'invalidate':
        case 'remove': {
          if (!removeOnInvalidate) return

          const items = Array.isArray(args[0]) ? args[0] : [args[0]]
          after(() => {
            for (const item of items) {
              const keyArr = Array.isArray(item)
                ? (item as readonly unknown[])
                : item?.key
              if (Array.isArray(keyArr)) {
                void remove(keyArr)
              }
            }
          })
          break
        }
      }
    })
  }
}
